/**
 * Dashboard - Cottagecore redesign
 * Two states: empty (no ideas) and populated (has ideas).
 * All business logic (hooks, navigation, auth) preserved from original.
 */
import React, { useState, useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { useIdeas, useDeleteIdea } from '../hooks/api/useIdeas';
import DeleteIdeaConfirmModal from '../components/DeleteIdeaConfirmModal';
import { useTasks } from '../hooks/api/useTasks';
import { useGapsSummary } from '../hooks/api/useGaps';
import { useApp } from '../contexts/AppContext';
import { useAuth } from '../contexts/AuthContext';
import { useSubscription } from '../contexts/SubscriptionContext';
import { formatTaskDate } from '../utils/dateUtils';
import { cleanName } from '../utils/textUtils';

// ─── Design tokens ────────────────────────────────────────────────────────────
const T = {
  paper:      '#F7EFD8',
  paperDeep:  '#EEE2BF',
  paperSoft:  '#FDF7E5',
  card:       '#FFFBEC',
  ink:        '#2E2109',
  ink2:       '#4A3510',
  inkMuted:   '#6E5A38',
  inkFaint:   '#A39068',
  rule:       '#D7C9A1',
  ruleStrong: '#BFAB79',
  orange:     '#EA7C1F',
  orangeDark: '#C25F0E',
  moss:       '#6B7A3E',
  mossDark:   '#4E5A29',
  mossSoft:   '#D7DCB9',
  capRed:     '#C14A33',
  capRedSoft: '#F2C7BA',
  roof:       '#C76A3B',
  roofSoft:   '#F2D1B3',
  r2: '6px', r3: '10px', r4: '14px', full: '999px',
  sh1: '0 1px 0 rgba(46,33,9,0.06), 0 2px 4px rgba(46,33,9,0.04)',
  sh2: '0 2px 0 rgba(46,33,9,0.04), 0 8px 16px -8px rgba(46,33,9,0.14)',
  sans: '"Nunito", ui-sans-serif, system-ui, -apple-system, sans-serif',
  hand: '"Caveat", "Nunito", cursive',
  mono: '"JetBrains Mono", ui-monospace, "SF Mono", Menlo, monospace',
};

// ─── Helpers ──────────────────────────────────────────────────────────────────

const firstNameOf = (user) => {
  if (!user) return 'there';
  const full = user.full_name || user.name || user.email || '';
  return full.split(' ')[0] || 'there';
};

const ideaStatusBadge = (status) => {
  switch (status) {
    case 'active':
    case 'specified':
      return { label: 'active', bg: T.mossSoft, color: T.mossDark };
    case 'in_discovery':
      return { label: 'discovering', bg: T.roofSoft, color: T.roof };
    case 'draft':
      return { label: 'draft', bg: T.paperDeep, color: T.inkMuted };
    default:
      return { label: status || 'draft', bg: T.paperDeep, color: T.inkMuted };
  }
};

// ─── Shared primitives ────────────────────────────────────────────────────────

const StatusPill = ({ label, bg, color }) => (
  <span style={{
    display: 'inline-block',
    padding: '2px 8px',
    borderRadius: T.full,
    background: bg,
    color,
    fontSize: '11px',
    fontWeight: 700,
    letterSpacing: '0.4px',
    fontFamily: T.sans,
    flexShrink: 0,
  }}>
    {label}
  </span>
);

const GhostBtn = ({ children, onClick, style = {} }) => {
  const [hov, setHov] = useState(false);
  return (
    <button
      onClick={onClick}
      onMouseEnter={() => setHov(true)}
      onMouseLeave={() => setHov(false)}
      style={{
        background: 'none',
        border: 'none',
        cursor: 'pointer',
        fontFamily: T.sans,
        fontSize: '13px',
        fontWeight: 700,
        color: hov ? T.orangeDark : T.orange,
        padding: '4px 0',
        transition: 'color 0.15s',
        ...style,
      }}
    >
      {children}
    </button>
  );
};

const OrangeBtn = ({ children, onClick, fullWidth = false, style = {} }) => {
  const [hov, setHov] = useState(false);
  return (
    <button
      onClick={onClick}
      onMouseEnter={() => setHov(true)}
      onMouseLeave={() => setHov(false)}
      style={{
        display: 'block',
        width: fullWidth ? '100%' : 'auto',
        padding: '11px 20px',
        background: hov ? T.orangeDark : T.orange,
        color: '#fff',
        border: 'none',
        borderRadius: T.r3,
        fontFamily: T.sans,
        fontSize: '15px',
        fontWeight: 800,
        cursor: 'pointer',
        transition: 'background 0.15s',
        textAlign: 'center',
        ...style,
      }}
    >
      {children}
    </button>
  );
};

// ─── Loading skeleton ─────────────────────────────────────────────────────────

const LoadingSkeleton = () => (
  <div style={{ padding: '40px 32px', maxWidth: 900, margin: '0 auto' }}>
    <style>{`
      @keyframes cc-pulse {
        0%, 100% { opacity: 1; }
        50% { opacity: 0.45; }
      }
    `}</style>
    {[1, 2, 3].map((i) => (
      <div key={i} style={{
        height: 20,
        borderRadius: T.r3,
        background: T.paperDeep,
        marginBottom: 14,
        animation: 'cc-pulse 1.4s ease-in-out infinite',
        animationDelay: `${i * 0.15}s`,
      }} />
    ))}
  </div>
);

// ─── Empty state ──────────────────────────────────────────────────────────────

const STEPS = [
  ['1', 'Describe an idea', "A sentence, rough notes, a wall of text — whatever you have. We'll build a spec and surface our assumptions."],
  ['2', 'Review the gaps', "We flag ambiguities and risks. You decide what matters."],
  ['3', 'Get a real plan', 'Features and stories — structured enough to build from, or hand off.'],
];

const EmptyDashboard = ({ userName, onCreateIdea }) => (
  <div style={{ padding: '40px', maxWidth: 900, margin: '0 auto', fontFamily: T.sans }}>
    {/* Header */}
    <div style={{ marginBottom: 28 }}>
      <div style={{
        fontFamily: T.hand,
        fontSize: 26,
        color: T.orange,
        marginBottom: 6,
      }}>
        welcome in, {userName} —
      </div>
      <h1 style={{
        margin: 0,
        fontSize: 34,
        fontWeight: 800,
        color: T.ink,
        lineHeight: 1.2,
        fontFamily: T.sans,
      }}>
        Let's plant your first idea.
      </h1>
    </div>

    {/* Main card */}
    <div style={{
      background: T.card,
      borderRadius: T.r4,
      boxShadow: T.sh2,
      border: `1px solid ${T.rule}`,
      overflow: 'hidden',
      marginBottom: 16,
    }}>
      {/* Top section */}
      <div style={{
        padding: '22px 28px',
        borderBottom: `1px dashed ${T.rule}`,
        background: T.paperSoft,
      }}>
        <p style={{
          margin: 0,
          fontSize: 15,
          color: T.ink2,
          lineHeight: 1.6,
          fontFamily: T.sans,
        }}>
          Three steps. Takes about two minutes. Describe what you want to build — Cahoots turns it into a structured spec, surfaces assumptions and risks, then breaks it into features and stories.
        </p>
      </div>

      {/* Steps + CTA */}
      <div style={{ padding: '28px' }}>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 14, marginBottom: 20 }}>
          {STEPS.map(([num, title, desc]) => (
            <div key={num} style={{
              display: 'flex',
              alignItems: 'flex-start',
              gap: 14,
              padding: '14px 18px',
              border: `1px dashed ${T.rule}`,
              borderRadius: T.r3,
              background: T.paperSoft,
            }}>
              {/* Number circle */}
              <div style={{
                width: 30,
                height: 30,
                borderRadius: T.full,
                background: T.card,
                border: `1.5px solid ${T.orange}`,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                flexShrink: 0,
                fontFamily: T.sans,
                fontSize: 13,
                fontWeight: 800,
                color: T.orange,
              }}>
                {num}
              </div>
              <div>
                <div style={{
                  fontSize: 15,
                  fontWeight: 800,
                  color: T.ink,
                  fontFamily: T.sans,
                  marginBottom: 2,
                }}>
                  {title}
                </div>
                <div style={{
                  fontSize: 13,
                  color: T.ink2,
                  fontFamily: T.sans,
                  lineHeight: 1.5,
                }}>
                  {desc}
                </div>
              </div>
            </div>
          ))}
        </div>

        <OrangeBtn onClick={onCreateIdea} fullWidth>
          + Plant your first idea
        </OrangeBtn>
      </div>
    </div>

  </div>
);

// ─── Stat card ─────────────────────────────────────────────────────────────────

const StatCard = ({ label, value, trend, trendColor }) => (
  <div style={{
    flex: '1 1 0',
    background: T.card,
    borderRadius: T.r4,
    boxShadow: T.sh2,
    border: `1px solid ${T.rule}`,
    padding: '20px',
    fontFamily: T.sans,
    minWidth: 0,
  }}>
    <div style={{
      fontSize: 11,
      fontWeight: 800,
      letterSpacing: '0.8px',
      color: T.inkMuted,
      textTransform: 'uppercase',
      marginBottom: 6,
    }}>
      {label}
    </div>
    <div style={{
      fontSize: 36,
      fontWeight: 800,
      color: T.ink,
      letterSpacing: '-0.8px',
      lineHeight: 1.1,
      marginBottom: 4,
    }}>
      {value}
    </div>
    <div style={{
      fontSize: 12,
      color: trendColor || T.moss,
      fontFamily: T.sans,
    }}>
      {trend}
    </div>
  </div>
);

// ─── Populated dashboard ───────────────────────────────────────────────────────

// TODAY_STACK is built dynamically from real idea data — see useTodayStack

const TypeBadge = ({ label, bg, color }) => (
  <span style={{
    display: 'inline-block',
    padding: '2px 7px',
    borderRadius: T.r2,
    background: bg,
    color,
    fontSize: '10px',
    fontWeight: 800,
    letterSpacing: '0.6px',
    fontFamily: T.sans,
    flexShrink: 0,
  }}>
    {label}
  </span>
);

const PopulatedDashboard = ({ ideas, gapsSummary, userName, navigate, onRefetchIdeas }) => {
  const { showSuccess, showError } = useApp();
  const deleteIdeaMutation = useDeleteIdea();
  const [pendingDelete, setPendingDelete] = useState(null);

  const handleConfirmDelete = async () => {
    if (!pendingDelete) return;
    try {
      await deleteIdeaMutation.mutateAsync(pendingDelete.id);
      showSuccess('Idea deleted.');
      setPendingDelete(null);
      if (onRefetchIdeas) await onRefetchIdeas();
    } catch (err) {
      showError(err?.message || 'Failed to delete idea.');
    }
  };

  // Computed stats
  const repoCount = useMemo(
    () => ideas.filter(i => (i.github_repos || []).length > 0).length,
    [ideas]
  );
  const firstRepo = useMemo(() => {
    const withRepo = ideas.find(i => (i.github_repos || []).length > 0);
    if (!withRepo) return null;
    const r = withRepo.github_repos[0];
    return r ? (r.repo ? `${r.owner}/${r.repo}` : r.url || null) : null;
  }, [ideas]);

  const gapsCount = gapsSummary?.total ?? 0;
  const gapsNeedAttention = gapsSummary?.needs_attention ?? 0;

  const driftCount = useMemo(
    () => ideas.filter(i => (i.drift_mismatch_count ?? 0) > 0).length,
    [ideas]
  );

  // Build "Needs attention" from real data: drift mismatch counts + per-idea gap counts
  const todayStack = useMemo(() => {
    const items = [];

    // DRIFT — one row per idea with unresolved spec mismatches
    for (const idea of ideas) {
      const mismatches = idea.drift_mismatch_count ?? 0;
      if (mismatches === 0) continue;
      const name = (idea.name || 'Untitled').replace(/^(Build|Create|Make|Add)\s+/i, '');
      items.push({
        type: 'DRIFT',
        typeColor: T.capRed,
        typeBg: T.capRedSoft,
        title: name,
        sub: `${mismatches} mismatch${mismatches !== 1 ? 'es' : ''} · spec vs code`,
        ideaId: idea.id,
      });
    }

    // GAP — one row per idea that has open gaps
    for (const idea of ideas) {
      const gaps = idea.gap_count ?? 0;
      if (gaps === 0) continue;
      const name = (idea.name || 'Untitled').replace(/^(Build|Create|Make|Add)\s+/i, '');
      items.push({
        type: 'GAP',
        typeColor: T.orangeDark,
        typeBg: T.roofSoft,
        title: name,
        sub: `${gaps} open gap${gaps !== 1 ? 's' : ''} · unanswered questions`,
        ideaId: idea.id,
      });
    }

    return items.slice(0, 5);
  }, [ideas]);

  const previewIdeas = ideas.slice(0, 4);

  return (
    <div style={{
      padding: '28px 32px 40px',
      display: 'flex',
      flexDirection: 'column',
      gap: 24,
      fontFamily: T.sans,
    }}>
      {/* Header row */}
      <div style={{ display: 'flex', alignItems: 'flex-end', justifyContent: 'space-between' }}>
        <div>
          <div style={{
            fontFamily: T.hand,
            fontSize: 28,
            color: T.orange,
            lineHeight: 1.1,
            marginBottom: 4,
          }}>
            morning, {userName} —
          </div>
          <h1 style={{
            margin: 0,
            fontSize: 34,
            fontWeight: 800,
            color: T.ink,
            lineHeight: 1.1,
            fontFamily: T.sans,
          }}>
            {ideas.length} {ideas.length === 1 ? 'idea' : 'ideas'} in cahoots.
          </h1>
        </div>
      </div>

      {/* Stat cards row */}
      <div style={{ display: 'flex', gap: 16, flexWrap: 'wrap' }}>
        <StatCard
          label="Open ideas"
          value={ideas.length}
          trend="living plans"
          trendColor={T.moss}
        />
        <StatCard
          label="Gaps flagged"
          value={gapsCount}
          trend={gapsNeedAttention > 0 ? 'needs your attention' : 'all clear'}
          trendColor={gapsNeedAttention > 0 ? T.capRed : T.moss}
        />
        <StatCard
          label="Drift"
          value={driftCount}
          trend={driftCount > 0 ? 'spec \u2194 code mismatch' : 'no drift'}
          trendColor={driftCount > 0 ? T.capRed : T.moss}
        />
        <StatCard
          label="Repo"
          value={repoCount}
          trend={firstRepo || 'connect a repo'}
          trendColor={repoCount > 0 ? T.moss : T.inkMuted}
        />
      </div>

      {/* Two-column grid */}
      <div style={{
        display: 'grid',
        gridTemplateColumns: '1.5fr 1fr',
        gap: 20,
        alignItems: 'start',
      }}>
        {/* LEFT — Your ideas */}
        <div style={{
          background: T.card,
          borderRadius: T.r4,
          boxShadow: T.sh2,
          border: `1px solid ${T.rule}`,
          overflow: 'hidden',
        }}>
          {/* Card header */}
          <div style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            padding: '18px 20px',
            borderBottom: `1px dashed ${T.rule}`,
          }}>
            <span style={{ fontSize: 20, fontWeight: 800, color: T.ink, fontFamily: T.sans }}>
              Your ideas
            </span>
            <GhostBtn onClick={() => navigate('/ideas')}>
              See all &rarr;
            </GhostBtn>
          </div>

          {/* Idea rows */}
          {previewIdeas.map((idea, idx) => {
            const badge = ideaStatusBadge(idea.status);
            return (
              <IdeaRow
                key={idea.id}
                idea={idea}
                badge={badge}
                isLast={idx === previewIdeas.length - 1}
                onClick={() => navigate(`/ideas/${idea.id}`)}
                onRequestDelete={setPendingDelete}
                deleting={deleteIdeaMutation.isPending && pendingDelete?.id === idea.id}
              />
            );
          })}

          {/* Footer CTA */}
          <div style={{ padding: 14 }}>
            <OrangeBtn onClick={() => navigate('/ideas/create')} fullWidth>
              + New idea
            </OrangeBtn>
          </div>
        </div>

        {/* RIGHT — Today's stack */}
        <div style={{
          background: T.card,
          borderRadius: T.r4,
          boxShadow: T.sh2,
          border: `1px solid ${T.rule}`,
          overflow: 'hidden',
        }}>
          {/* Card header */}
          <div style={{
            padding: '18px 20px',
            borderBottom: `1px dashed ${T.rule}`,
          }}>
            <div style={{ fontSize: 20, fontWeight: 800, color: T.ink, fontFamily: T.sans, marginBottom: 3 }}>
              Needs attention
            </div>
            <div style={{ fontSize: 12, color: T.inkMuted, fontFamily: T.sans }}>
              Drift and open gaps across your ideas.
            </div>
          </div>

          {/* Stack items */}
          <div style={{ padding: '8px 0' }}>
            {todayStack.length === 0 ? (
              <div style={{
                padding: '24px 20px',
                textAlign: 'center',
                color: T.inkFaint,
                fontSize: 13,
                fontFamily: T.sans,
              }}>
                <div style={{ fontSize: 20, marginBottom: 8 }}>✓</div>
                <div style={{ fontWeight: 600, color: T.inkMuted, marginBottom: 4 }}>All clear</div>
                <div style={{ fontSize: 12 }}>No drift or gaps to review right now.</div>
              </div>
            ) : todayStack.map((item, idx) => (
              <div
                key={idx}
                onClick={item.ideaId ? () => navigate(`/ideas/${item.ideaId}?tab=${item.type === 'DRIFT' ? 'github' : 'gaps'}`) : undefined}
                style={{
                  display: 'flex',
                  alignItems: 'flex-start',
                  gap: 12,
                  padding: '13px 20px',
                  borderBottom: idx < todayStack.length - 1 ? `1px dashed ${T.rule}` : 'none',
                  cursor: item.ideaId ? 'pointer' : 'default',
                  transition: 'background 0.1s',
                }}
                onMouseEnter={(e) => { if (item.ideaId) e.currentTarget.style.background = T.paperDeep; }}
                onMouseLeave={(e) => { e.currentTarget.style.background = 'transparent'; }}
              >
                <TypeBadge label={item.type} bg={item.typeBg} color={item.typeColor} />
                <div style={{ minWidth: 0 }}>
                  <div style={{
                    fontSize: 13,
                    fontWeight: 700,
                    color: T.ink,
                    fontFamily: T.sans,
                    marginBottom: 2,
                    whiteSpace: 'nowrap',
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                  }}>
                    {item.title}
                  </div>
                  <div style={{
                    fontSize: 11,
                    color: T.inkMuted,
                    fontFamily: T.mono,
                    whiteSpace: 'nowrap',
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                  }}>
                    {item.sub}
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>

      <DeleteIdeaConfirmModal
        idea={pendingDelete}
        isOpen={!!pendingDelete}
        onCancel={() => !deleteIdeaMutation.isPending && setPendingDelete(null)}
        onConfirm={handleConfirmDelete}
        busy={deleteIdeaMutation.isPending}
      />
    </div>
  );
};

// ─── Idea row (inside "Your ideas" card) ──────────────────────────────────────

const IdeaRow = ({ idea, badge, isLast, onClick, onRequestDelete, deleting }) => {
  const [hov, setHov] = useState(false);
  const handleDeleteClick = (e) => {
    e.stopPropagation();
    if (deleting) return;
    onRequestDelete?.(idea);
  };
  return (
    <div
      onClick={onClick}
      onMouseEnter={() => setHov(true)}
      onMouseLeave={() => setHov(false)}
      style={{
        display: 'flex',
        alignItems: 'center',
        gap: 14,
        padding: '16px 20px',
        borderBottom: isLast ? 'none' : `1px dashed ${T.rule}`,
        cursor: 'pointer',
        background: hov ? T.paperSoft : 'transparent',
        transition: 'background 0.12s',
      }}
    >
      {/* Status badge */}
      <StatusPill label={badge.label} bg={badge.bg} color={badge.color} />

      {/* Title + one-liner */}
      <div style={{ flex: 1, minWidth: 0 }}>
        <div style={{
          fontSize: 15,
          fontWeight: 800,
          color: T.ink,
          fontFamily: T.sans,
          whiteSpace: 'nowrap',
          overflow: 'hidden',
          textOverflow: 'ellipsis',
          marginBottom: 2,
        }}>
          {cleanName(idea.name) || 'Untitled Idea'}
        </div>
        {idea.one_liner && (
          <div style={{
            fontSize: 13,
            color: T.ink2,
            fontFamily: T.sans,
            whiteSpace: 'nowrap',
            overflow: 'hidden',
            textOverflow: 'ellipsis',
          }}>
            {idea.one_liner}
          </div>
        )}
      </div>

      {/* Last touched */}
      <div style={{
        fontSize: 11,
        color: T.inkMuted,
        fontFamily: T.mono,
        flexShrink: 0,
        whiteSpace: 'nowrap',
      }}>
        {formatTaskDate(idea.updated_at || idea.created_at)}
      </div>

      {/* Delete button (shown on hover) */}
      {onRequestDelete && (
        <button
          type="button"
          onClick={handleDeleteClick}
          disabled={deleting}
          aria-label="Delete idea"
          title="Delete idea"
          style={{
            flexShrink: 0,
            width: 28, height: 28,
            border: 'none',
            background: hov ? 'rgba(193,74,51,0.10)' : 'transparent',
            color: hov ? '#C14A33' : T.inkFaint,
            borderRadius: 6,
            cursor: deleting ? 'default' : 'pointer',
            opacity: hov ? 1 : 0,
            transition: 'opacity 0.12s, background 0.12s, color 0.12s',
            fontSize: 14,
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            fontFamily: T.sans,
          }}
        >
          ✕
        </button>
      )}
    </div>
  );
};

// ─── Main export ──────────────────────────────────────────────────────────────

const Dashboard = () => {
  const navigate = useNavigate();
  const { showError } = useApp();
  const { user } = useAuth();
  const { canUseGitHub } = useSubscription();

  // Data fetching — preserve all original hook calls
  const {
    data: ideasData,
    isLoading: ideasLoading,
    error: ideasError,
    refetch: refetchIdeas,
  } = useIdeas();

  const {
    data: plansData,
    isLoading: plansLoading,
    error: plansError,
  } = useTasks({ page: 1, pageSize: 5, topLevelOnly: true });

  const {
    data: gapsSummary,
  } = useGapsSummary();

  // Error handling — eslint-disable to match original pattern
  useEffect(() => {
    if (ideasError) showError(ideasError.message || 'Failed to load ideas');
    if (plansError) showError(plansError.message || 'Failed to load plans');
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [ideasError, plansError]);

  const ideas = ideasData?.items || [];
  const recentPlans = plansData?.items || [];
  const isLoading = ideasLoading || plansLoading;

  // Derived stats — preserved from original
  const stats = useMemo(() => ({
    total: ideas.length,
    specified: ideas.filter(p => p.status === 'specified' || p.status === 'active').length,
    inDiscovery: ideas.filter(p => p.status === 'in_discovery').length,
    totalPlans: ideas.reduce((sum, p) => sum + (p.plan_count || 0), 0),
  }), [ideas]);

  const handlePlanClick = (planId) => navigate(`/plans/${planId}`);

  const userName = firstNameOf(user);

  if (isLoading) return <LoadingSkeleton />;

  if (ideas.length === 0) {
    return (
      <EmptyDashboard
        userName={userName}
        onCreateIdea={() => navigate('/ideas/create')}
      />
    );
  }

  return (
    <PopulatedDashboard
      ideas={ideas}
      gapsSummary={gapsSummary}
      userName={userName}
      navigate={navigate}
      onRefetchIdeas={refetchIdeas}
    />
  );
};

export default Dashboard;
